import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppState } from "../../state/appState";
import { buildNotificationsFromState } from "../../utils/notifications";
import {
  selectBmsDevice,
  selectDashboardPdmDevices,
} from "../../state/selectors";
import DashboardView from "../dashboard/DashboardView";
import DevicesView from "../devices/DevicesView";
import NetworkView from "../network/NetworkView";
import ConfigView from "../config/ConfigView";
import DeviceConfigOverlay from "../devices/DeviceConfigOverlay";
import BmsConfigOverlay from "../devices/BmsConfigOverlay";
import AutoCreatePrompt from "../../components/AutoCreatePrompt";
import AddChannelDialog from "../devices/AddChannelDialog";
import Icon from "../../components/ui/Icon";
import { ReactComponent as CberryLogo } from "../../assets/cberry-logo.svg";
import { BRAND } from "../../brand";
import { TAB_FADE_MS, OVERLAY_CLEAR_DELAY_MS } from "../../constants/app";
import { NOTIF_STYLES } from "../../styles/notificationStyles";
import ErrorBoundary from "../../components/shared/ErrorBoundary";

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard" },
  { id: "devices", label: "Devices", icon: "electrical_services" },
  { id: "network", label: "Network", icon: "hub" },
  { id: "config", label: "Config", icon: "tune" },
];


export default function AppShell() {
  const { devices, nodes, discoveryStatus, lastScanAt, telemetry, ui, actions, updatedFirmware, gatewayConnected } = useAppState();

  // Tab navigation
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isFading, setIsFading] = useState(false);
  const fadeTimeoutRef = useRef(null);
  const contentRef = useRef(null);

  // Notifications — derived from live state
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { notifications, regularNodes } = useMemo(
    () => buildNotificationsFromState({ updatedFirmware, nodes, devices }),
    [updatedFirmware, nodes, devices]
  );
  const nodesScanned = regularNodes.length > 0;

  // PDM config overlay
  const [pdmConfigDeviceId, setPdmConfigDeviceId] = useState(null);
  const [isPdmConfigOpen, setIsPdmConfigOpen] = useState(false);

  // BMS config overlay
  const [isBmsOverlayOpen, setIsBmsOverlayOpen] = useState(false);
  const [bmsConfig, setBmsConfig] = useState({ name: "Main Battery", showOnDashboard: false });

  // EWL channel creation dialogs
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Deep-link into a ConfigView sub-page from other tabs
  const [configInitialPage, setConfigInitialPage] = useState(null);

  // Derived state
  const bmsDevice = useMemo(() => selectBmsDevice(devices), [devices]);
  const ewlNodes = useMemo(() => nodes.filter((n) => n.subtype === "EWL-810"), [nodes]);
  const pendingPromptNode = useMemo(
    () => nodes.find((n) => ui?.autoCreatePrompts?.[n.id]),
    [nodes, ui]
  );
  const dashboardDevices = useMemo(() => selectDashboardPdmDevices(devices), [devices]);

  const hasNotifications = gatewayConnected && nodesScanned && notifications.length > 0;
  const hasCriticalNotifications = notifications.some((n) => n.severity === "critical");
  const networkHasAlerts = nodes.some((node) => node.status !== "OK");
  const fridgeFaultActive = devices.some((d) => d.id === "fridge" && d.status === "fault");
  const hasSystemAlerts = hasCriticalNotifications || networkHasAlerts || fridgeFaultActive;

  const pdmDevice = useMemo(
    () => devices.find((d) => d.id === pdmConfigDeviceId),
    [devices, pdmConfigDeviceId]
  );

  // Mapping preset: __inUse maps "busLabel::Ch N" → ownerDeviceId; owned keys show active state for current device
  const mappingPreset = useMemo(() => {
    const inUse = {};
    const owned = {};
    ewlNodes.forEach((n) => {
      (n.channels || []).forEach((ch) => {
        const busLabel = n.name || n.id;
        const chLabel = `Ch ${ch.index}`;
        const key = `${busLabel}::${chLabel}`;
        if (ch.ownerDeviceId) {
          inUse[key] = ch.ownerDeviceId;
          if (pdmConfigDeviceId && ch.ownerDeviceId === pdmConfigDeviceId) {
            owned[busLabel] = { ...(owned[busLabel] || {}), [chLabel]: true };
          }
        }
      });
    });
    return { ...owned, __inUse: inUse };
  }, [pdmConfigDeviceId, ewlNodes]);

  // Fade tab transition
  const performTabSwitch = useCallback(
    (tab) => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      setIsFading(true);
      fadeTimeoutRef.current = setTimeout(() => {
        setActiveTab(tab);
        setIsPdmConfigOpen(false);
        setPdmConfigDeviceId(null);
        setIsBmsOverlayOpen(false);
        requestAnimationFrame(() => {
          setIsFading(false);
          if (contentRef.current) contentRef.current.scrollTop = 0;
        });
      }, TAB_FADE_MS);
    },
    []
  );

  const handleNavigateToGateway = useCallback(() => {
    setConfigInitialPage("gateway");
    performTabSwitch("config");
  }, [performTabSwitch]);

  const handleTabSelect = useCallback(
    (tab) => {
      if (tab === activeTab) {
        if (contentRef.current) contentRef.current.scrollTop = 0;
        return;
      }
      performTabSwitch(tab);
    },
    [activeTab, performTabSwitch]
  );

  // Open PDM config overlay
  const handleOpenPdmConfig = useCallback((id) => {
    setPdmConfigDeviceId(id);
    setIsPdmConfigOpen(true);
  }, []);

  // Close PDM config overlay
  const handleClosePdmConfig = useCallback(() => {
    setIsPdmConfigOpen(false);
    setTimeout(() => setPdmConfigDeviceId(null), OVERLAY_CLEAR_DELAY_MS);
  }, []);

  // Save PDM config — reconcile channel mappings then persist meta
  const handleSavePdmConfig = useCallback((patch) => {
    if (!pdmConfigDeviceId) return;
    actions.reconcileChannelMapping(pdmConfigDeviceId, patch.desiredMappings || []);
    actions.updateMeta(pdmConfigDeviceId, patch);
    handleClosePdmConfig();
  }, [pdmConfigDeviceId, actions, handleClosePdmConfig]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  return (
    <div
      style={{
        backgroundColor: BRAND.navy,
        fontFamily: "'Inter Tight', -apple-system, sans-serif",
      }}
      className="min-h-screen w-full flex items-center justify-center p-4"
    >
      <div className="relative w-[414px] max-w-full h-[896px] max-h-[90vh] rounded-[40px] border border-brand-purple bg-brand-navy shadow-[0_20px_60px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center px-5 pt-5 pb-2 gap-3 flex-shrink-0">
          <div className="flex items-center justify-start flex-1 min-w-[70px]">
            <div
              className={`relative flex h-11 w-11 items-center justify-center rounded-full border ${
                hasSystemAlerts
                  ? "border-red-500 bg-red-900/40"
                  : "border-emerald-500 bg-emerald-900/40"
              }`}
            >
              <Icon
                name={hasSystemAlerts ? "error" : "check_circle"}
                className={`text-xl ${hasSystemAlerts ? "text-red-300" : "text-emerald-300"}`}
              />
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center min-w-0 pt-2">
            <div className="flex items-center gap-2">
              <CberryLogo className="h-8 w-auto" aria-label="cBerry" />
              <span
                className="text-xl font-black italic tracking-tight"
                style={{ color: BRAND.light }}
              >
                cBerry
              </span>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end min-w-[70px]">
            <button
              type="button"
              onClick={() => setIsNotificationsOpen(true)}
              className="relative flex h-11 w-11 items-center justify-center rounded-full hover:opacity-80 transition"
              style={{ backgroundColor: BRAND.purple, color: BRAND.light }}
              aria-label="Notifications"
            >
              <Icon name="notifications" className="text-xl" />
              {hasNotifications && (
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500" />
              )}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden relative min-h-0">
          {/* Scrollable content area (dashboard / devices — NOT network or config) */}
          <div
            ref={contentRef}
            className="h-full overflow-y-auto transition-opacity duration-200"
            style={{
              opacity: isFading ? 0 : 1,
              display: (activeTab === "network" || activeTab === "config") ? "none" : undefined,
              overflowY:
                isPdmConfigOpen || isBmsOverlayOpen ? "hidden" : "auto",
            }}
          >
            {activeTab === "dashboard" && (
              <ErrorBoundary label="Dashboard">
              <div className="px-5 pb-4 space-y-6">
                <DashboardView
                  devices={dashboardDevices}
                  bmsDevice={bmsDevice}
                  telemetry={telemetry}
                  onToggle={actions.toggleDevice}
                  onDim={actions.setDim}
                />
              </div>
              </ErrorBoundary>
            )}

            {activeTab === "devices" && (
              <ErrorBoundary label="Devices">
              <div className="px-5 pb-4 space-y-6">
                <DevicesView
                  devices={devices}
                  nodes={nodes}
                  onToggle={actions.toggleDevice}
                  onDim={actions.setDim}
                  onConfigure={handleOpenPdmConfig}
                  onCreatePdm={() => setIsAddDialogOpen(true)}
                  onOpenBms={(id) => {
                    const bms = devices.find((d) => d.id === id);
                    if (bms) {
                      setBmsConfig({ name: bms.name, showOnDashboard: bms.showOnDashboard });
                    }
                    setIsBmsOverlayOpen(true);
                  }}
                />
              </div>
              </ErrorBoundary>
            )}
          </div>

          {/* Network tab — fills full height, manages own scrolling */}
          {activeTab === "network" && (
            <ErrorBoundary label="Network">
            <div
              className="absolute inset-0 transition-opacity duration-200"
              style={{ opacity: isFading ? 0 : 1 }}
            >
              <NetworkView
                nodes={nodes}
                status={discoveryStatus}
                lastScanAt={lastScanAt}
                onStartDiscovery={actions.startDiscovery}
                onNavigateToGateway={handleNavigateToGateway}
                onAddChannels={() => setIsAddDialogOpen(true)}
              />
            </div>
            </ErrorBoundary>
          )}

          {/* Config tab — fills full height, manages own scrolling and sub-page overlays */}
          {activeTab === "config" && (
            <ErrorBoundary label="Config">
            <div
              className="absolute inset-0 transition-opacity duration-200"
              style={{ opacity: isFading ? 0 : 1 }}
            >
              <ConfigView
                initialPage={configInitialPage ?? "main"}
                onConsumeInitialPage={() => setConfigInitialPage(null)}
              />
            </div>
            </ErrorBoundary>
          )}

          {/* PDM Config overlay — self-contained with mapping awareness */}
          <DeviceConfigOverlay
            isOpen={isPdmConfigOpen}
            device={pdmDevice}
            onClose={handleClosePdmConfig}
            onSave={handleSavePdmConfig}
            mappingBuses={ewlNodes}
            mappingPreset={mappingPreset}
            mappingNodes={ewlNodes}
          />

          {/* BMS overlay (devices tab only) */}
          {activeTab === "devices" && (
            <BmsConfigOverlay
              isOpen={isBmsOverlayOpen}
              device={bmsDevice}
              bmsName={bmsConfig.name}
              bridgeName={nodes.find((n) => n.type === "BMS")?.name}
              showOnDashboard={bmsConfig.showOnDashboard}
              onNameChange={(value) =>
                setBmsConfig((prev) => ({ ...prev, name: value }))
              }
              onShowOnDashboardChange={(next) =>
                setBmsConfig((prev) => ({ ...prev, showOnDashboard: next }))
              }
              onSave={() => {
                if (bmsDevice) {
                  actions.updateMeta(bmsDevice.id, {
                    name: bmsConfig.name,
                    showOnDashboard: bmsConfig.showOnDashboard,
                  });
                }
                setIsBmsOverlayOpen(false);
              }}
              onClose={() => setIsBmsOverlayOpen(false)}
            />
          )}
        </div>

        {/* Auto-create prompt — fires once per EWL after scan */}
        <AutoCreatePrompt
          node={pendingPromptNode}
          isOpen={Boolean(pendingPromptNode)}
          ewlCount={ewlNodes.length}
          onConfirm={(nodeId, nodeName, count) => {
            actions.createPdmChannels({ nodeId, nodeName, count });
          }}
          onDecline={(nodeId) => actions.markPrompted(nodeId)}
        />

        {/* Manual add-channel dialog */}
        <AddChannelDialog
          isOpen={isAddDialogOpen}
          nodes={nodes}
          onClose={() => setIsAddDialogOpen(false)}
          onConfirm={({ mode, selections, aggregate }) => {
            if (mode === "auto") {
              for (const ewl of ewlNodes) {
                const created = actions.createPdmChannels({
                  nodeId: ewl.id,
                  nodeName: ewl.name,
                  count: 1,
                });
                if (created) break;
              }
            } else {
              if (aggregate) {
                const slots = [];
                (selections || []).forEach(({ nodeId, indices }) => {
                  const node = nodes.find((n) => n.id === nodeId);
                  (indices || []).forEach((idx) => {
                    const ch = node?.channels?.find((c) => c.index === idx && !c.ownerDeviceId);
                    if (ch) slots.push({ nodeId, nodeName: node?.name, index: idx });
                  });
                });
                if (slots.length > 0) actions.createAggregateDevice(slots);
              } else {
                (selections || []).forEach(({ nodeId, indices }) => {
                  const node = nodes.find((n) => n.id === nodeId);
                  actions.createPdmChannels({
                    nodeId,
                    nodeName: node?.name,
                    indices: indices || [],
                  });
                });
              }
            }
            setIsAddDialogOpen(false);
          }}
        />

        {/* Footer nav */}
        <nav
          className="flex h-20 items-center justify-between px-1 flex-shrink-0"
          style={{ borderTop: `1px solid ${BRAND.purple}`, backgroundColor: BRAND.navy }}
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabSelect(tab.id)}
                className="flex flex-col items-center justify-center gap-1 flex-1 py-1"
              >
                <span
                  className="text-xl"
                  style={{ color: active ? BRAND.magenta : `${BRAND.light}80` }}
                >
                  <Icon name={tab.icon} />
                </span>
                <span
                  className="text-xs font-medium tracking-wide"
                  style={{ color: active ? BRAND.magenta : `${BRAND.light}80` }}
                >
                  {tab.label}
                </span>
                {active && (
                  <span
                    className="mt-1 h-0.5 w-10 rounded-full"
                    style={{ backgroundColor: BRAND.magenta }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Notifications overlay */}
        {isNotificationsOpen && (
          <div
            className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-14 px-4"
            onClick={() => setIsNotificationsOpen(false)}
          >
            <div
              className="w-full rounded-3xl border border-brand-purple bg-brand-navy/95 shadow-2xl max-h-[78%] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-brand-purple bg-brand-navy/90 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold tracking-[0.2em] text-brand-light uppercase">
                    Notifications
                  </span>
                  {gatewayConnected && nodesScanned && notifications.length > 0 && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-magenta/15 text-brand-magenta border border-brand-magenta/30">
                      {notifications.length}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsNotificationsOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple text-brand-light/70 hover:bg-brand-purple/80 transition"
                >
                  <Icon name="close" className="text-base" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
                {/* No gateway */}
                {!gatewayConnected && (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple">
                      <Icon name="bluetooth_disabled" className="text-2xl text-brand-light/40" />
                    </span>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-brand-light">No gateway connected</p>
                      <p className="text-xs text-brand-light/50 max-w-[220px] mx-auto">
                        Connect to a cBerry gateway to receive system notifications.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsNotificationsOpen(false);
                        setConfigInitialPage("gateway");
                        performTabSwitch("config");
                      }}
                      className="rounded-xl bg-brand-magenta px-4 py-2.5 text-xs font-semibold text-white hover:bg-brand-pink transition"
                    >
                      Connect to Gateway
                    </button>
                  </div>
                )}

                {/* Gateway connected, no scan */}
                {gatewayConnected && !nodesScanned && (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple">
                      <Icon name="hub" className="text-2xl text-brand-light/40" />
                    </span>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-brand-light">Network not scanned</p>
                      <p className="text-xs text-brand-light/50 max-w-[220px] mx-auto">
                        Scan the CAN bus to see device notifications.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsNotificationsOpen(false);
                        actions.startDiscovery();
                        performTabSwitch("network");
                      }}
                      className="rounded-xl bg-brand-magenta px-4 py-2.5 text-xs font-semibold text-white hover:bg-brand-pink transition"
                    >
                      Scan Network
                    </button>
                  </div>
                )}

                {/* All clear */}
                {gatewayConnected && nodesScanned && notifications.length === 0 && (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 border border-emerald-500/30">
                      <Icon name="check_circle" className="text-2xl text-emerald-400" />
                    </span>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-brand-light">All systems OK</p>
                      <p className="text-xs text-brand-light/50">No active alerts on this network.</p>
                    </div>
                  </div>
                )}

                {/* Notification cards */}
                {gatewayConnected && nodesScanned && notifications.map((n) => {
                  const s = NOTIF_STYLES[n.severity] ?? NOTIF_STYLES.info;
                  return (
                    <div
                      key={n.id}
                      className={`rounded-2xl border ${s.border} ${s.bg} px-3 py-3 flex items-start gap-3`}
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full mt-0.5 ${s.iconBg}`}>
                        <Icon name={n.icon} className={`text-sm ${s.iconColor}`} />
                      </span>
                      <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[9px] font-black italic tracking-widest uppercase px-1.5 py-0.5 rounded-full ${s.chipBg} ${s.chipText}`}>
                            {s.chipLabel}
                          </span>
                          <span className="text-xs font-semibold text-brand-light">{n.title}</span>
                        </div>
                        <p className="text-[11px] text-brand-light/60 leading-snug">{n.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
